import type {
  MarketingPageContent,
  MarketingPageKey,
} from './marketing-page-types';

// Public-page copy captured on October 5, 2026. Layout is defined separately.
// Studio uses the user's approved $199; legal wording and dates are unchanged.
export const marketingContent: Record<MarketingPageKey, MarketingPageContent> =
  {
    about: {
      documentTitle: 'About - DirectorDeck',
      title: 'About DirectorDeck',
      intro: [],
      sections: [
        {
          title: 'Our Mission',
          body: [
            {
              tag: 'p',
              children: [
                'We believe every presentation deserves to be memorable. DirectorDeck was born from a simple frustration: why do most presentations look the same when the stories they tell are unique?',
              ],
            },
          ],
        },
        {
          title: 'What We Do',
          body: [
            {
              tag: 'p',
              children: [
                'DirectorDeck transforms ordinary presentations into cinematic experiences. Using advanced technology, we generate custom transitions and professional layouts that make your content shine.',
              ],
            },
          ],
        },
        {
          title: 'Who We Serve',
          body: [
            {
              tag: 'p',
              children: [
                'From TED speakers to startup founders, from educators to corporate executives - anyone who wants their presentations to captivate their audience.',
              ],
            },
          ],
        },
        {
          title: 'Our Approach',
          body: [
            {
              tag: 'p',
              children: [
                'We believe in simplicity. Upload your presentation, click transform, and download your cinematic masterpiece. No design skills required. No complex software to learn.',
              ],
            },
          ],
        },
        {
          title: 'Get in Touch',
          body: [
            {
              tag: 'p',
              children: [
                "Have questions? We'd love to hear from you. Reach out at",
                ' ',
                {
                  tag: 'a',
                  children: ['hello@directordeck.com'],
                  href: 'mailto:hello@directordeck.com',
                },
                '.',
              ],
            },
          ],
        },
      ],
      lede: 'Transforming presentations into cinematic experiences',
    },
    contact: {
      documentTitle: 'Contact - DirectorDeck',
      title: 'Contact Us',
      intro: [
        {
          tag: 'p',
          children: ['Have a question? Need help? Want to share feedback?'],
        },
        {
          tag: 'p',
          children: [
            'Reach out to us at',
            ' ',
            {
              tag: 'a',
              children: ['hello@directordeck.com'],
              href: 'mailto:hello@directordeck.com',
            },
          ],
        },
      ],
      sections: [
        {
          title: 'General Inquiries',
          body: [
            {
              tag: 'p',
              children: ['Questions about DirectorDeck, pricing, or features.'],
            },
            {
              tag: 'a',
              children: ['hello@directordeck.com'],
              href: 'mailto:hello@directordeck.com',
            },
          ],
        },
        {
          title: 'Support',
          body: [
            {
              tag: 'p',
              children: ['Technical issues or account help.'],
            },
            {
              tag: 'a',
              children: ['support@directordeck.com'],
              href: 'mailto:support@directordeck.com',
            },
          ],
        },
      ],
      lede: "We'd love to hear from you",
    },
    faq: {
      documentTitle: 'FAQ - DirectorDeck',
      title: 'FAQ',
      intro: [],
      sections: [],
      lede: 'Frequently Asked Questions',
      questions: [
        {
          question: 'What types of presentations work best?',
          answer:
            'DirectorDeck works with any presentation, but shines brightest with TED talks, keynote speeches, and pitch decks where visual impact matters most.',
        },
        {
          question: 'How do credits work?',
          answer:
            'Credits are our simple pricing model. Each Studio user adds 3,000 shared credits every month. Active Studio subscribers can add 1,000 credits for $100 whenever they need more.',
        },
        {
          question: 'What makes the transitions "cinematic"?',
          answer:
            'Unlike generic transitions, ours are custom-crafted for each slide. The motion, timing, and effects are designed specifically for your content - no two are exactly alike.',
        },
        {
          question: 'Can I try before I buy?',
          answer:
            'Yes! New accounts receive free credits to experience the transformation firsthand.',
        },
        {
          question: 'What file formats do you support?',
          answer:
            'Upload PowerPoint (.pptx) files. Export your transformed presentation as PPTX, PDF, or video.',
        },
        {
          question: 'Can my team share credits?',
          answer: 'Yes, Studio subscriptions include shared credit pools.',
        },
        {
          question: 'Is my data secure?',
          answer:
            'Absolutely. We use industry-standard encryption for data in transit and at rest. Your presentations are stored securely on Google Cloud Platform.',
        },
        {
          question: 'Can I cancel my subscription anytime?',
          answer:
            "Yes, you can cancel your subscription at any time from your account settings. You'll continue to have access until the end of your billing period.",
        },
      ],
    },
    terms: {
      documentTitle: 'Terms of Service - DirectorDeck',
      title: 'Terms of Service',
      intro: [
        {
          tag: 'p',
          children: [
            'These Terms of Service (“Terms”) are a contract between you and DirectorDeck (“DirectorDeck,” “we,” “us,” or “our”) and govern your access to and use of our websites, applications, and related services (the “Service”). By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
          ],
        },
        {
          tag: 'p',
          children: [
            'We may change these Terms at our discretion. If we do, we will update the effective date. Changes apply prospectively. Your continued use after changes means you accept the updated Terms.',
          ],
        },
      ],
      sections: [
        {
          title: '1. Eligibility',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['1.1 Age.'],
                },
                ' The Service is for individuals 18 years or older. By using the Service, you represent that you are at least 18 years old.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['1.2 Export and sanctions.'],
                },
                ' You are not located in, under the control of, or a national or resident of any country or person subject to U.S. sanctions or export restrictions. You will comply with applicable export and sanctions laws.',
              ],
            },
          ],
        },
        {
          title: '2. Accounts and Access',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['2.1 Account registration.'],
                },
                ' To use the Service, you must create an account. You agree to provide accurate, complete registration information and keep it current.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['2.2 Account security.'],
                },
                ' You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us promptly of any suspected unauthorized use.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['2.3 Studios (Teams).'],
                },
                ' You may create or join Studios (team workspaces). Studio owners and administrators are responsible for managing access and ensuring members comply with these Terms.',
              ],
            },
          ],
        },
        {
          title: '3. The Service',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['3.1 Description.'],
                },
                ' DirectorDeck provides presentation transformation services. Users upload presentation files, and the Service analyzes, redesigns slides, and generates cinematic transitions. The Service operates on a credits-based model.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['3.2 License to use.'],
                },
                ' Subject to these Terms, we grant you a limited, revocable, non-exclusive, non-transferable license to access and use the Service for your personal or internal business purposes.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['3.3 Generated content.'],
                },
                ' Content generated by the Service (redesigned slides, transitions, videos) is licensed to you for your use. You may use generated content in your presentations and share them as you see fit, subject to these Terms.',
              ],
            },
          ],
        },
        {
          title: '4. Credits and Payments',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['4.1 Credits.'],
                },
                ' Credits are the currency used to access paid features within the Service. Different operations consume different amounts of credits.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['4.2 Purchases.'],
                },
                ' You may purchase credits a la carte or through subscription plans. Subscription credits are granted at the start of each billing period.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['4.3 Refunds.'],
                },
                ' Purchased credits are non-refundable except where required by law. Unused subscription credits may be subject to expiration or rollover limits as specified in your plan.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['4.4 Taxes.'],
                },
                ' You are responsible for any applicable taxes. Prices displayed may not include taxes, which will be added at checkout where required.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['4.5 Payment processing.'],
                },
                " Payments are processed by Stripe. By making a purchase, you agree to Stripe's terms of service.",
              ],
            },
          ],
        },
        {
          title: '5. Your Content',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['5.1 Ownership.'],
                },
                ' You retain ownership of the presentations and other content you upload to the Service (“Your Content”). DirectorDeck does not claim ownership of Your Content.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['5.2 License to DirectorDeck.'],
                },
                ' You grant DirectorDeck a worldwide, non-exclusive, royalty-free license to host, store, process, and display Your Content solely to provide and improve the Service. This license terminates when you delete Your Content or your account.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['5.3 Your responsibilities.'],
                },
                ' You represent that you have all necessary rights to Your Content and that it does not violate any third-party rights, including intellectual property, privacy, or publicity rights.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['5.4 Prohibited content.'],
                },
                ' You will not upload content that is illegal, infringing, defamatory, obscene, threatening, or otherwise objectionable. We reserve the right to remove content that violates these Terms.',
              ],
            },
          ],
        },
        {
          title: '6. Acceptable Use',
          body: [
            {
              tag: 'p',
              children: ['You agree not to:'],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    'Use the Service for any unlawful purpose or in violation of these Terms',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Attempt to gain unauthorized access to the Service or its systems',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Interfere with or disrupt the Service or servers or networks connected to the Service',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Reverse engineer, decompile, or attempt to extract the source code of the Service',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Use automated means (bots, scrapers) to access the Service except as we expressly allow',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Resell, rent, lease, or sublicense access to the Service without our written permission',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    'Circumvent any rate limits, usage caps, or technical safeguards',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: '7. Intellectual Property',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['7.1 Our property.'],
                },
                ' The Service, including software, designs, text, graphics, logos, and other content (excluding Your Content), is owned by DirectorDeck or our licensors and protected by intellectual property laws. No rights are granted except as expressly set forth in these Terms.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['7.2 Trademarks.'],
                },
                ' DirectorDeck, our logo, and other marks are trademarks of DirectorDeck. You may not use our trademarks without prior written permission.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['7.3 Feedback.'],
                },
                ' If you provide feedback or suggestions about the Service, you grant us a perpetual, irrevocable, royalty-free license to use them without restriction or compensation.',
              ],
            },
          ],
        },
        {
          title: '8. Third-Party Services',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['8.1 Service providers.'],
                },
                " We use third-party services to operate the Service, including cloud hosting, authentication, payments, and content generation. Your use of the Service may be subject to those providers' terms.",
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['8.2 Third-party links.'],
                },
                ' The Service may contain links to third-party websites or services. We are not responsible for their content or practices.',
              ],
            },
          ],
        },
        {
          title: '9. Termination',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['9.1 By you.'],
                },
                ' You may stop using the Service and delete your account at any time through account settings.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['9.2 By us.'],
                },
                ' We may suspend or terminate your access to the Service at any time, with or without notice, for any reason, including violation of these Terms.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['9.3 Effect of termination.'],
                },
                ' Upon termination, your license to use the Service ends. We may delete Your Content after a reasonable period. Sections that by their nature should survive (including Sections 5, 7, 10-14) will survive termination.',
              ],
            },
          ],
        },
        {
          title: '10. Disclaimers',
          body: [
            {
              tag: 'p',
              children: [
                'THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ACCURATE, RELIABLE, ERROR-FREE, SECURE, OR UNINTERRUPTED.',
              ],
            },
            {
              tag: 'p',
              children: [
                'GENERATED CONTENT (REDESIGNED SLIDES, TRANSITIONS) MAY CONTAIN ERRORS OR NOT MEET YOUR EXPECTATIONS. YOU ARE SOLELY RESPONSIBLE FOR REVIEWING AND USING GENERATED CONTENT.',
              ],
            },
          ],
        },
        {
          title: '11. Limitation of Liability',
          body: [
            {
              tag: 'p',
              children: ['TO THE MAXIMUM EXTENT PERMITTED BY LAW:'],
            },
            {
              tag: 'p',
              children: [
                '(a) WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, GOODWILL, DATA, OR OTHER INTANGIBLE LOSSES, EVEN IF FORESEEABLE OR WE WERE ADVISED OF THE POSSIBILITY.',
              ],
            },
            {
              tag: 'p',
              children: [
                '(b) OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE OR THESE TERMS IS LIMITED TO THE GREATER OF (i) USD $100 OR (ii) THE AMOUNTS YOU PAID TO US FOR THE SERVICE IN THE THREE MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.',
              ],
            },
          ],
        },
        {
          title: '12. Indemnification',
          body: [
            {
              tag: 'p',
              children: [
                "To the fullest extent permitted by law, you will defend, indemnify, and hold harmless DirectorDeck and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) Your Content; (b) your use of the Service; (c) your violation of these Terms; or (d) your violation of any law or third-party right.",
              ],
            },
          ],
        },
        {
          title: '13. Dispute Resolution',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['13.1 Informal resolution.'],
                },
                ' Before filing a claim, you agree to contact us at hello@directordeck.com to attempt to resolve the dispute informally for 30 days.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['13.2 Arbitration.'],
                },
                ' If not resolved informally, any dispute arising out of or relating to these Terms or the Service will be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. Arbitration will be conducted by a single arbitrator seated in Seattle, Washington.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['13.3 Class action waiver.'],
                },
                ' You and DirectorDeck agree that each may bring claims only in your or its individual capacity and not as a plaintiff or class member in any purported class or representative proceeding.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['13.4 Opt-out.'],
                },
                ' You may opt out of arbitration within 30 days of first accepting these Terms by emailing hello@directordeck.com with the subject “Arbitration Opt-Out” and your account email.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['13.5 Small claims.'],
                },
                ' Either party may bring an individual action in small claims court.',
              ],
            },
          ],
        },
        {
          title: '14. General',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['14.1 Governing law.'],
                },
                ' These Terms are governed by the laws of the State of Washington, without regard to conflict-of-laws principles, except that the U.S. Federal Arbitration Act governs Section 13.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['14.2 Entire agreement.'],
                },
                ' These Terms constitute the entire agreement between you and us regarding the Service and supersede prior agreements.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['14.3 Severability.'],
                },
                " If any provision is held unenforceable, it will be modified to reflect the parties' intent; the remaining provisions remain in effect.",
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['14.4 No waiver.'],
                },
                ' Our failure to enforce any provision is not a waiver.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['14.5 Assignment.'],
                },
                ' You may not assign these Terms without our consent. We may assign these Terms without restriction.',
              ],
            },
          ],
        },
        {
          title: '15. Contact',
          body: [
            {
              tag: 'p',
              children: [
                'For questions about these Terms, please contact us at hello@directordeck.com.',
              ],
            },
          ],
        },
      ],
      effectiveDate: 'Effective date: January 2026',
    },
    privacy: {
      documentTitle: 'Privacy Policy - DirectorDeck',
      title: 'Privacy Policy',
      intro: [
        {
          tag: 'p',
          children: [
            'This Privacy Policy describes how DirectorDeck (“DirectorDeck,” “we,” “us,” or “our”) collects, uses, and shares information when you use our websites, applications, and services (the “Service”).',
          ],
        },
        {
          tag: 'p',
          children: [
            'By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree, please do not use the Service.',
          ],
        },
      ],
      sections: [
        {
          title: '1. Information We Collect',
          body: [
            {
              tag: 'h3',
              children: ['Information You Provide'],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Account information:'],
                    },
                    ' When you create an account, we collect your email address, name, and password.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Presentations:'],
                    },
                    ' When you use the Service, you upload presentation files (PowerPoint, PDF, etc.) for processing.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Payment information:'],
                    },
                    ' When you purchase credits or subscriptions, our payment processor (Stripe) collects payment details. We receive only limited information such as the last four digits of your card, card type, and billing address.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Communications:'],
                    },
                    ' When you contact us, we collect information you provide in your messages.',
                  ],
                },
              ],
            },
            {
              tag: 'h3',
              children: ['Information Collected Automatically'],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Usage data:'],
                    },
                    ' With your permission, we collect information about how you use the Service, including pages visited, features used, and actions taken.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Device information:'],
                    },
                    ' We collect device type, operating system, browser type, and IP address.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Cookies:'],
                    },
                    ' We use essential technology for authentication and preferences. Optional analytics and browser diagnostics run only after you opt in. See our Cookie Policy for details.',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: '2. How We Use Your Information',
          body: [
            {
              tag: 'p',
              children: ['We use the information we collect to:'],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Provide the Service:'],
                    },
                    ' Process your presentations, generate redesigned slides and transitions, and deliver results.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Manage your account:'],
                    },
                    ' Create and maintain your account, authenticate your access, and manage Studios (teams).',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Process payments:'],
                    },
                    ' Handle credit purchases, subscriptions, and billing.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Communicate with you:'],
                    },
                    ' Send transactional emails, respond to inquiries, and provide customer support.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Improve the Service:'],
                    },
                    ' With your permission, analyze usage patterns and browser errors to help develop new features.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Ensure security:'],
                    },
                    ' Detect and prevent fraud, abuse, and security threats.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Comply with legal obligations:'],
                    },
                    ' Respond to legal requests and enforce our Terms of Service.',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: '3. How We Share Your Information',
          body: [
            {
              tag: 'p',
              children: [
                'We do not sell your personal information. We may share information in the following circumstances:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Service providers:'],
                    },
                    ' We share information with third-party providers who help us operate the Service, including cloud hosting (Google Cloud Platform), authentication (Firebase), and payments (Stripe). PostHog analytics and Sentry browser diagnostics receive data only after you opt in. These providers are contractually required to protect your information.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Content generation:'],
                    },
                    ' Your uploaded presentations are processed by AI services to generate redesigned slides and transitions. This processing is necessary to provide the Service.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Studios (Teams):'],
                    },
                    ' If you join a Studio, information such as your name and email may be visible to other Studio members.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Legal requirements:'],
                    },
                    ' We may disclose information if required by law, legal process, or government request, or to protect our rights, property, or safety.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Business transfers:'],
                    },
                    ' If DirectorDeck is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: '4. Your Presentations',
          body: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['Processing:'],
                },
                ' When you upload a presentation, we process it to analyze slides, extract colors, generate redesigns, and create transitions. This processing is performed on secure cloud infrastructure.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['Storage:'],
                },
                ' Presentations and generated content are stored securely on Google Cloud Platform with encryption at rest and in transit.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['Access:'],
                },
                ' Only you and members of your Studio (if applicable) can access your presentations. Our support team may access presentations if you request assistance, with your permission.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['Deletion:'],
                },
                ' You can delete presentations at any time through the Service. When you delete your account, we will delete your presentations and associated data, subject to reasonable backup retention periods.',
              ],
            },
          ],
        },
        {
          title: '5. Data Retention',
          body: [
            {
              tag: 'p',
              children: [
                'We retain your information for as long as your account is active or as needed to provide the Service. After account deletion, we may retain certain information as required by law, to resolve disputes, or for legitimate business purposes (such as preventing fraud).',
              ],
            },
          ],
        },
        {
          title: '6. Data Security',
          body: [
            {
              tag: 'p',
              children: [
                'We implement appropriate technical and organizational measures to protect your information, including:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: ['Encryption of data in transit (TLS) and at rest'],
                },
                {
                  tag: 'li',
                  children: ['Access controls and authentication'],
                },
                {
                  tag: 'li',
                  children: ['Regular security assessments'],
                },
                {
                  tag: 'li',
                  children: [
                    'Secure cloud infrastructure (Google Cloud Platform)',
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'No system is completely secure. While we strive to protect your information, we cannot guarantee absolute security.',
              ],
            },
          ],
        },
        {
          title: '7. Your Rights and Choices',
          body: [
            {
              tag: 'p',
              children: ['Depending on your location, you may have rights to:'],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Access:'],
                    },
                    ' Request a copy of the personal information we hold about you.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Correction:'],
                    },
                    ' Request correction of inaccurate information.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Deletion:'],
                    },
                    ' Request deletion of your personal information.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Portability:'],
                    },
                    ' Request a copy of your data in a portable format.',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Opt-out:'],
                    },
                    ' Opt out of marketing communications at any time.',
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'To exercise these rights, contact us at hello@directordeck.com. We may need to verify your identity before processing your request.',
              ],
            },
          ],
        },
        {
          title: '8. Cookies',
          body: [
            {
              tag: 'p',
              children: [
                'We use essential cookies and similar technology for authentication and remembering preferences. Optional analytics and browser diagnostics are off until you opt in. Use Privacy choices on the website to change or withdraw that permission at any time. You can also control cookies through your browser settings, but disabling essential cookies may affect the Service.',
              ],
            },
            {
              tag: 'p',
              children: [
                'See our ',
                {
                  tag: 'a',
                  children: ['Cookie Policy'],
                  href: '/cookies',
                },
                ' for detailed information.',
              ],
            },
          ],
        },
        {
          title: '9. Third-Party Services',
          body: [
            {
              tag: 'p',
              children: [
                'The Service uses the following third-party services:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Firebase:'],
                    },
                    ' Authentication and user management',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Stripe:'],
                    },
                    ' Payment processing',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Google Cloud Platform:'],
                    },
                    ' Hosting and storage',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['PostHog:'],
                    },
                    ' Optional usage analytics and session recording',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Sentry:'],
                    },
                    ' Optional browser error reporting and error replay',
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'These services have their own privacy policies. We encourage you to review them.',
              ],
            },
          ],
        },
        {
          title: '10. International Transfers',
          body: [
            {
              tag: 'p',
              children: [
                'Your information may be transferred to and processed in countries other than your own, including the United States. These countries may have different data protection laws. Contact us at hello@directordeck.com for information about the safeguards that apply to these transfers.',
              ],
            },
          ],
        },
        {
          title: "11. Children's Privacy",
          body: [
            {
              tag: 'p',
              children: [
                'The Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it.',
              ],
            },
          ],
        },
        {
          title: '12. Changes to This Policy',
          body: [
            {
              tag: 'p',
              children: [
                'We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by posting a notice on the Service. Where a change requires new permission for optional processing, we will ask you to choose again.',
              ],
            },
          ],
        },
        {
          title: '13. Contact Us',
          body: [
            {
              tag: 'p',
              children: [
                'If you have questions about this Privacy Policy or our data practices, please contact us at:',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'strong',
                  children: ['Email:'],
                },
                ' hello@directordeck.com',
              ],
            },
          ],
        },
      ],
      effectiveDate: 'Effective date: October 12, 2026',
    },
    cookies: {
      documentTitle: 'Cookie Policy - DirectorDeck',
      title: 'Cookie Policy',
      intro: [
        {
          tag: 'p',
          children: [
            'This Cookie Policy explains how DirectorDeck uses cookies and similar technologies when you visit our websites and use our services (the “Service”).',
          ],
        },
      ],
      sections: [
        {
          title: 'What Are Cookies',
          body: [
            {
              tag: 'p',
              children: [
                'Cookies are small text files stored on your device when you visit websites. They help websites remember information about your visit and provide a better user experience.',
              ],
            },
          ],
        },
        {
          title: 'How We Use Cookies',
          body: [
            {
              tag: 'p',
              children: [
                'DirectorDeck uses cookies for the following purposes:',
              ],
            },
            {
              tag: 'h3',
              children: ['Essential Cookies'],
            },
            {
              tag: 'p',
              children: [
                'These cookies are necessary for the Service to function and cannot be disabled. They include:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Authentication:'],
                    },
                    ' To keep you signed in and secure your session',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Security:'],
                    },
                    ' To protect against fraud and unauthorized access',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Load balancing:'],
                    },
                    ' To distribute traffic and ensure reliability',
                  ],
                },
              ],
            },
            {
              tag: 'h3',
              children: ['Your Cookie Choice'],
            },
            {
              tag: 'p',
              children: [
                'Our essential directordeck_consent_v2 cookie remembers your separate choices for analytics and browser error diagnostics. It expires after 180 days. You can change either choice at any time using the Privacy choices control on the website.',
              ],
            },
            {
              tag: 'h3',
              children: ['Analytics and Error Diagnostics'],
            },
            {
              tag: 'p',
              children: [
                'You can allow these purposes separately. With your permission for analytics, PostHog helps us understand how visitors interact with the Service so we can improve it. This can include session recordings; form inputs are masked. With your permission for error diagnostics, Sentry helps us diagnose browser errors and performance issues and may record the surrounding session after an error. Each service stays off unless you allow its purpose:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Usage patterns:'],
                    },
                    ' Pages visited, features used, time spent',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Performance:'],
                    },
                    ' Page load times and errors',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Device information:'],
                    },
                    ' Browser type, screen size, operating system',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: 'Third-Party Services',
          body: [
            {
              tag: 'p',
              children: [
                'These providers may use cookies or similar technology when their services are active:',
              ],
            },
            {
              tag: 'table',
              children: [
                {
                  tag: 'thead',
                  children: [
                    {
                      tag: 'tr',
                      children: [
                        {
                          tag: 'th',
                          children: ['Service'],
                        },
                        {
                          tag: 'th',
                          children: ['Purpose'],
                        },
                        {
                          tag: 'th',
                          children: ['Category'],
                        },
                      ],
                    },
                  ],
                },
                {
                  tag: 'tbody',
                  children: [
                    {
                      tag: 'tr',
                      children: [
                        {
                          tag: 'td',
                          children: ['Firebase'],
                        },
                        {
                          tag: 'td',
                          children: ['Authentication'],
                        },
                        {
                          tag: 'td',
                          children: ['Essential'],
                        },
                      ],
                    },
                    {
                      tag: 'tr',
                      children: [
                        {
                          tag: 'td',
                          children: ['Stripe'],
                        },
                        {
                          tag: 'td',
                          children: ['Payment processing'],
                        },
                        {
                          tag: 'td',
                          children: ['Essential'],
                        },
                      ],
                    },
                    {
                      tag: 'tr',
                      children: [
                        {
                          tag: 'td',
                          children: ['PostHog'],
                        },
                        {
                          tag: 'td',
                          children: ['Usage analytics'],
                        },
                        {
                          tag: 'td',
                          children: ['Analytics'],
                        },
                      ],
                    },
                    {
                      tag: 'tr',
                      children: [
                        {
                          tag: 'td',
                          children: ['Sentry'],
                        },
                        {
                          tag: 'td',
                          children: [
                            'Browser errors, performance traces, and error replay',
                          ],
                        },
                        {
                          tag: 'td',
                          children: ['Error diagnostics'],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'These third parties have their own privacy and cookie policies. We encourage you to review them.',
              ],
            },
          ],
        },
        {
          title: 'Managing Cookies',
          body: [
            {
              tag: 'p',
              children: [
                'Use the Privacy choices control on any page to accept, reject, or change analytics and browser diagnostics consent. Reject all is available on the first banner. Both optional purposes are off by default. You can also delete cookies through your browser settings; disabling essential cookies may affect the Service.',
              ],
            },
            {
              tag: 'h3',
              children: ['Browser Settings'],
            },
            {
              tag: 'p',
              children: [
                'Most browsers allow you to view, manage, and delete cookies. The process varies by browser:',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Chrome:'],
                    },
                    ' Settings > Privacy and security > Cookies and other site data',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Firefox:'],
                    },
                    ' Settings > Privacy & Security > Cookies and Site Data',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Safari:'],
                    },
                    ' Preferences > Privacy > Manage Website Data',
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'strong',
                      children: ['Edge:'],
                    },
                    ' Settings > Cookies and site permissions > Cookies and site data',
                  ],
                },
              ],
            },
          ],
        },
        {
          title: 'Do Not Track',
          body: [
            {
              tag: 'p',
              children: [
                'Some browsers include a “Do Not Track” feature. Use our Privacy choices control to set or withdraw your analytics and browser diagnostics permission; a browser Do Not Track signal alone does not set this choice.',
              ],
            },
          ],
        },
        {
          title: 'Changes to This Policy',
          body: [
            {
              tag: 'p',
              children: [
                'We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated effective date.',
              ],
            },
          ],
        },
        {
          title: 'Contact Us',
          body: [
            {
              tag: 'p',
              children: [
                'For questions about our use of cookies, please contact us at hello@directordeck.com.',
              ],
            },
          ],
        },
      ],
      effectiveDate: 'Effective date: October 12, 2026',
    },
  };
